<template>
    <div>
        <pc-empty-placeholder v-if="mapUnavailable">
            <template v-slot:image>
                <map-unavailable-state-image />
            </template>
            <div class="d-flex flex-column mt-2 align-items-center">
                <span class="fst-italic my-1 text-primary-brand-light">{{ serviceUnavailableMessage }}</span>
                <pc-transition>
                    <pc-button-icon
                        v-if="showRetryButton"
                        outline
                        class="btn-outline-primary mt-3"
                        icon="arrow-clockwise"
                        v-on:click="onRetryClick">
                        {{ retryButtonLabel }}
                    </pc-button-icon>
                </pc-transition>
            </div>
        </pc-empty-placeholder>
        <div
            v-show="!mapUnavailable"
            v-bind:id="internalId"
            class="w-100 h-100" />
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from "vue";
    import { globalGenerator } from "Util/uniqueIdGenerator";
    import mapUnavailableStateImage from "Images/icons/emptyStateImagery/empty_map.vue";
    import { KEYS, NAMESPACE } from "./i18n/pcMap.i18n";
    import { useResource } from "Util/i18n";
    import * as atlas from "azure-maps-control";
    import "azure-maps-control/dist/atlas.min.css";

    const props = defineProps({
        // Configuration to use. Optional, since manual initialize accepts config as a parameter
        config: { type: Object, required: false, default: null }, // see pcMapConfig.js

        // True if there should be an option to retry if the map fails to load
        allowServiceRetry: { type: Boolean, required: false, default: true },
        // Delay before showing retry button again after user attempts to retry service connection
        retryTimeout: { type: Number, required: false, default: 5000 },

        // The id of the map. If not provided, a unique id will be generated
        id: { type: String, required: false, default: null },

        // True if the map should not be initialized automatically
        manualInitialize: { type: Boolean, required: false, default: false }
    });

    const emit = defineEmits(["initialize-failed", "retry-init-button-clicked"]);

    const internalId = ref(props.id || globalGenerator.getNextId("pcMap"));
    const mapObject = ref(null);
    const mapUnavailable = ref(false);
    const hideRetryButtonForDelay = ref(false);

    const showRetryButton = computed(() => props.allowServiceRetry && !hideRetryButtonForDelay.value);

    // Map Control Methods

    function initialize(config) {
        // Initialize the map
        mapUnavailable.value = false;

        // Sanity check authentication
        if (!config?.MapConfig?.authOptions?.sasToken) {
            onServiceUnavailable();
            return;
        }

        mapObject.value = new atlas.Map(internalId.value, config.MapConfig);

        config.OnSetup(mapObject.value);

        mapObject.value.events.add("ready", async () => config.OnReady(mapObject.value));
        mapObject.value.events.add("error", () => {
            onServiceUnavailable();
        });
    }

    function onServiceUnavailable() {
        // If the map fails to load, show the empty state
        mapUnavailable.value = true;
        suppressRetryButtonForTime();
        emit("initialize-failed");
    }

    function reinitialize() {
        if (mapUnavailable.value) {
            if (mapObject.value) {
                mapObject.value.dispose();
            }
            initialize(props.config);
        }
    }

    function resizeMap() {
        if (mapObject.value) {
            mapObject.value.resize();
        }
    }

    // Service Retry Methods
    function onRetryClick() {
        // The parent component may need to update the map config before retrying, so emit this event
        // for it to handle and wait for the parent to call reinitialize.
        emit("retry-init-button-clicked");
        suppressRetryButtonForTime();
    }

    function suppressRetryButtonForTime() {
        hideRetryButtonForDelay.value = true;
        setTimeout(() => {
            hideRetryButtonForDelay.value = false;
        }, props.retryTimeout);
    }

    // Lifecycle Events
    onMounted(() => {
        if (!props.manualInitialize) {
            initialize(props.config);
        }
    });

    // I18n
    const { retryButtonLabel, serviceUnavailableMessage } = useResource(KEYS, NAMESPACE);

    defineExpose({
        initialize,
        reinitialize,
        resizeMap
    });
</script>
